export interface OrderDetails {
  page_id?: string
  /* eslint-disable @typescript-eslint/naming-convention */
  data: {
    'first-name': string
    'last-name': string
    telephone: string
    email: string
    company: string
    department: string
    'account-ean'?: string
    'customer-po'?: string
    notes?: string
    'shipping-address-street': string
    'shipping-address-door': string
    'shipping-address-zip-code': string
    'shipping-address-city': string
    'shipping-address-state'?: string
    'shipping-address-country': string
    'billing-address-same-as-shipping': string
    'billing-address-street'?: string,
    'billing-address-door'?: string,
    'billing-address-zip-code'?: string,
    'billing-address-city'?: string,
    'billing-address-state'?: string,
    'billing-address-country'?: string,
  }
  /* eslint-enable @typescript-eslint/naming-convention */
}

/**
 * @property preventUpdate - If true, the order details update event won't be triggered
 */
type OrderDetailsClearParams = {
  preventUpdate: boolean;
};

const errorHandlers: Record<string, (error: Error) => string> = {
  /* eslint-disable @typescript-eslint/naming-convention */
  default: () => 'Unable to perform operation. Please try again.',
  SecurityError: () => 'Unable to access order details. Please ensure you are not using private browsing before retrying.',
  /* eslint-enable @typescript-eslint/naming-convention */
};

const handlerOrderDetailsError = (error: Error) => {
  if (error.name in errorHandlers) {
    return errorHandlers[error.name](error);
  }

  return errorHandlers.default(error);
};

const withErrorHandling = <T>(operation: () => T, errorHandling: () => T): Promise<T> => {
  try {
    const result = operation();
    return Promise.resolve(result);
  } catch (error) {
    // eslint-disable-next-line @typescript-eslint/no-use-before-define
    OrderDetailsService.errorMessage = handlerOrderDetailsError(error);
    return Promise.reject(errorHandling());
  }
};

export class OrderDetailsService {
  public static errorMessage: string;

  public static OrderDetailsUpdateEvent: string = 'OrderDetailsUpdate';

  private static readonly orderDetailsLocalStorageKey = 'order-details';

  public static getOrderDetails(): Promise<OrderDetails> {
    return withErrorHandling(
      () => {
        if (!localStorage.getItem(this.orderDetailsLocalStorageKey)) {
          localStorage.setItem(this.orderDetailsLocalStorageKey, JSON.stringify({}));
        }
        return JSON.parse(localStorage.getItem(this.orderDetailsLocalStorageKey)) as OrderDetails;
      },
      () => ({} as OrderDetails),
    );
  }

  public static saveDetails(details: OrderDetails): Promise<void> {
    return withErrorHandling(
      () => {
        localStorage.setItem(
          this.orderDetailsLocalStorageKey,
          JSON.stringify(details),
        );
        window.dispatchEvent(new Event(this.OrderDetailsUpdateEvent));
      },
      () => undefined,
    );
  }

  public static clearOrderDetails(params?: OrderDetailsClearParams): Promise<void> {
    return withErrorHandling(
      () => {
        localStorage.setItem(this.orderDetailsLocalStorageKey, JSON.stringify({}));
        if (params?.preventUpdate) {
          return;
        }
        window.dispatchEvent(new Event(this.OrderDetailsUpdateEvent));
      },
      () => undefined,
    );
  }
}
